import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { collectFee, updateFee, deleteFee, fetchFees } from "../../redux/admin/fees.thunk";
import { fetchStudents } from "../../redux/admin/student.thunk";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* =====================================================
   CONFIRMATION / INFO MODAL
   Replaces window.confirm() and blocking toastr alerts
   with a proper, accessible dialog.
===================================================== */
const ConfirmModal = ({ open, config, onClose }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    dialogRef.current?.focus();
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open || !config) return null;

  const { variant = "danger", title, message, confirmLabel, cancelLabel, onConfirm, hideCancel } = config;

  return (
    <div className="mnc-modal-overlay" role="presentation" onMouseDown={onClose}>
      <div
        className={`mnc-modal mnc-modal-${variant}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="mnc-modal-title"
        tabIndex={-1}
        ref={dialogRef}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={`mnc-modal-icon mnc-modal-icon-${variant}`}>
          {variant === "danger" ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9" />
              <line x1="12" y1="8" x2="12" y2="13" />
              <circle cx="12" cy="16.2" r="0.6" fill="currentColor" stroke="none" />
            </svg>
          )}
        </div>

        <h5 id="mnc-modal-title" className="mnc-modal-title">{title}</h5>
        <p className="mnc-modal-message">{message}</p>

        <div className="mnc-modal-actions">
          {!hideCancel && (
            <button type="button" className="btn-mnc-discard" onClick={onClose}>
              {cancelLabel || "Cancel"}
            </button>
          )}
          <button
            type="button"
            className={variant === "danger" ? "btn-mnc-danger" : "btn-mnc-sync"}
            onClick={() => {
              onConfirm?.();
              onClose();
            }}
          >
            {confirmLabel || "OK"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* =====================================================
   SEARCHABLE STUDENT COMBOBOX
===================================================== */
const StudentCombobox = ({ students, value, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selectedStudent = useMemo(
    () => students.find((s) => String(s.id) === String(value)),
    [students, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        String(s.admission_number || "").toLowerCase().includes(q)
    );
  }, [students, query]);

  useEffect(() => {
    if (!open) {
      setQuery(selectedStudent ? `${selectedStudent.admission_number} — ${selectedStudent.name}` : "");
    }
  }, [selectedStudent, open]);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const activeEl = listRef.current.children[activeIndex];
    if (activeEl) activeEl.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const commit = (student) => {
    onSelect(student ? String(student.id) : "");
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) commit(filtered[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery(selectedStudent ? `${selectedStudent.admission_number} — ${selectedStudent.name}` : "");
    }
  };

  return (
    <div className="mnc-combobox" ref={wrapperRef}>
      <div className={`mnc-combobox-field ${open ? "is-open" : ""}`}>
        <svg className="mnc-combobox-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M21 21l-3.8-3.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="mnc-field mnc-combobox-input fw-600"
          placeholder="Search student by name or admission number…"
          value={query}
          disabled={disabled}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
        />
        {value && !open && (
          <button
            type="button"
            className="mnc-combobox-clear"
            aria-label="Clear selected student"
            onClick={() => commit(null)}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <ul className="mnc-combobox-list" ref={listRef} role="listbox">
          {filtered.length === 0 && (
            <li className="mnc-combobox-empty">No students match “{query}”</li>
          )}
          {filtered.map((student, index) => (
            <li
              key={student.id}
              role="option"
              aria-selected={String(student.id) === String(value)}
              className={`mnc-combobox-option ${index === activeIndex ? "is-active" : ""} ${String(student.id) === String(value) ? "is-selected" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(student);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <span className="mnc-combobox-option-name">{student.name}</span>
              <span className="mnc-combobox-option-adm">{student.admission_number}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* A payment's "recorded period" is the bill it was logged under
   (bill_month/bill_year from the backend), which may differ from
   what it targets — an advance collected in July while crediting
   August still belongs to July's bill. Falls back to target_month/
   target_year for rows fetched before the backend returned bill_month/year. */
const getRecordedPeriod = (f) => ({
  month: Number(f.bill_month ?? f.target_month ?? f.month),
  year: Number(f.bill_year ?? f.target_year ?? f.year),
});

/* =====================================================
   MAIN FEE TERMINAL COMPONENT
===================================================== */
const Fees = () => {
  const dispatch = useDispatch();

  const { students = [] } = useSelector((state) => state.admin?.students || {});
  const { fees = [] } = useSelector((state) => state.admin?.fees || {});

  const [studentId, setStudentId] = useState("");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  // Input States
  const [baseFee, setBaseFee] = useState("4800");
  const [isBaseFeeCustom, setIsBaseFeeCustom] = useState(false); // Flag when user explicitly modifies base fee
  const [discount, setDiscount] = useState("0");
  const [advanceAdjusted, setAdvanceAdjusted] = useState("0");
  const [amountCollectingToday, setAmountCollectingToday] = useState("");
  const [advanceExtra, setAdvanceExtra] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [remarks, setRemarks] = useState("");

  const [selectedVoucherId, setSelectedVoucherId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // When a period is already fully settled, the collection form stays
  // collapsed by default — this flag lets the admin explicitly reopen it
  // to record an extra or advance payment anyway.
  const [forceShowCollectionForm, setForceShowCollectionForm] = useState(false);

  // Modal state — drives ConfirmModal for delete confirmations and advance-lock notices
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const closeModal = () => setModalOpen(false);
  const openModal = (config) => {
    setModalConfig(config);
    setModalOpen(true);
  };

  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" },
  ];

  const selectedStudent = useMemo(
    () => students.find((s) => String(s.id) === String(studentId)),
    [students, studentId]
  );

  useEffect(() => {
    dispatch(fetchStudents({ page: 1, limit: 1000, status: "active" }));
    dispatch(fetchFees({ page: 1, limit: 200, search: "" }));
  }, [dispatch]);

  /* =====================================================
     REAL-TIME AUTOMATED AUDIT CALCULATION ENGINE
  ===================================================== */
  const statementHistory = useMemo(() => {
    if (!studentId) {
      return { baseFee: 4800, discount: 0, advanceCredit: 0, directPaid: 0, totalCredit: 0, netPayable: 4800, remainingDue: 4800, payments: [] };
    }

    const targetM = Number(month);
    const targetY = Number(year);

    // Filter records actually logged under this month's bill
    const periodRecords = fees.filter((f) => {
      if (String(f.student_id) !== String(studentId)) return false;
      const period = getRecordedPeriod(f);
      return period.month === targetM && period.year === targetY;
    });

    const firstRecord = periodRecords[0];
    const dbBase = firstRecord ? parseFloat(firstRecord.base_fee || firstRecord.base_fee_amount || 4800) : 4800;
    const dbDisc = firstRecord ? parseFloat(firstRecord.discount || firstRecord.discount_amount || 0) : 0;

    // Use current active state input if user modified it, otherwise default to DB record
    const activeBase = isBaseFeeCustom ? (parseFloat(baseFee) || 0) : dbBase;
    const activeDisc = parseFloat(discount) || dbDisc;

    // Sum prior advances captured for this target month
    const advanceCreditSum = fees.reduce((sum, f) => {
      if (
        String(f.student_id) === String(studentId) &&
        f.payment_type === "advance" &&
        Number(f.target_month) === targetM &&
        Number(f.target_year) === targetY &&
        f.status !== "cancelled"
      ) {
        return sum + (parseFloat(f.amount || f.paid_amount) || 0);
      }
      return sum;
    }, 0);

    // Sum direct tuition payments for this month
    const directTuitionPaidSum = periodRecords.reduce((sum, r) => {
      if ((r.payment_type === "tuition" || !r.payment_type) && r.status !== "cancelled") {
        return sum + (parseFloat(r.amount || r.paid_amount) || 0);
      }
      return sum;
    }, 0);

    const netPayable = Math.max(0, activeBase - activeDisc);
    const totalCreditApplied = advanceCreditSum + directTuitionPaidSum;
    const due = Math.max(0, netPayable - totalCreditApplied);

    return {
      baseFee: activeBase,
      discount: activeDisc,
      advanceCredit: advanceCreditSum,
      directPaid: directTuitionPaidSum,
      totalCredit: totalCreditApplied,
      netPayable,
      remainingDue: due,
      payments: periodRecords,
    };
  }, [fees, studentId, month, year, baseFee, discount, isBaseFeeCustom]);

  // Sync inputs when student, month, or year changes
  useEffect(() => {
    if (studentId) {
      if (!isBaseFeeCustom) {
        setBaseFee(statementHistory.baseFee);
      }
      setDiscount(statementHistory.discount);
      setAdvanceAdjusted(statementHistory.advanceCredit);
      setAmountCollectingToday(statementHistory.remainingDue > 0 ? statementHistory.remainingDue : "");
    } else {
      setBaseFee("4800");
      setDiscount("0");
      setAdvanceAdjusted("0");
      setAmountCollectingToday("");
      setIsBaseFeeCustom(false);
    }
    setSelectedVoucherId(null);
    setForceShowCollectionForm(false);
  }, [studentId, month, year]);

  const financialSummary = useMemo(() => {
    const paidForCurrent = parseFloat(amountCollectingToday) || 0;
    const advExtra = parseFloat(advanceExtra) || 0;
    const totalReceiptPayable = paidForCurrent + advExtra;

    return {
      paidForCurrent,
      advExtra,
      netMonthPayable: statementHistory.netPayable,
      currentMonthDue: statementHistory.remainingDue,
      totalReceiptPayable,
      isPartialPayment: statementHistory.remainingDue > 0,
      isFullyCleared: statementHistory.remainingDue === 0 && statementHistory.baseFee > 0,
      hasAdvance: advExtra > 0,
    };
  }, [amountCollectingToday, advanceExtra, statementHistory]);

  /* =====================================================
     LIVE TRANSACTION PREVIEW
     Plain-language read-out of exactly what will be saved,
     so an admin can catch a wrong entry before submitting.
  ===================================================== */
  const transactionPreview = useMemo(() => {
    const { paidForCurrent, advExtra, currentMonthDue } = financialSummary;

    if (paidForCurrent <= 0 && advExtra <= 0) {
      return { tone: "muted", text: "Enter an amount to see what this voucher will record." };
    }

    const parts = [];
    let tone = "success";

    if (currentMonthDue === 0 && paidForCurrent > 0) {
      tone = "info";
      parts.push(`This month is already fully paid — ₹${paidForCurrent.toFixed(2)} will be recorded as an extra payment, not toward this month's due.`);
    } else if (paidForCurrent > currentMonthDue) {
      tone = "warning";
      const excess = (paidForCurrent - currentMonthDue).toFixed(2);
      parts.push(`Clears this month's ₹${currentMonthDue.toFixed(2)} due, with ₹${excess} extra. Consider moving the extra into "Advance Received for Future" instead.`);
    } else if (paidForCurrent === currentMonthDue && currentMonthDue > 0) {
      tone = "success";
      parts.push(`Full payment — this clears the month's ₹${currentMonthDue.toFixed(2)} due completely.`);
    } else if (paidForCurrent > 0 && paidForCurrent < currentMonthDue) {
      tone = "warning";
      const remaining = (currentMonthDue - paidForCurrent).toFixed(2);
      parts.push(`Partial payment — ₹${remaining} will remain pending for this month after saving.`);
    }

    if (advExtra > 0) {
      parts.push(`+ ₹${advExtra.toFixed(2)} will be credited as advance for a future month.`);
    }

    return { tone, text: parts.join(" ") };
  }, [financialSummary]);

  const handleReset = () => {
    setStudentId("");
    setBaseFee("4800");
    setDiscount("0");
    setAdvanceAdjusted("0");
    setAmountCollectingToday("");
    setAdvanceExtra("");
    setRemarks("");
    setSelectedVoucherId(null);
    setIsBaseFeeCustom(false);
    setForceShowCollectionForm(false);
  };

  const handleSwitchToNewPayment = () => {
    setSelectedVoucherId(null);
    setAmountCollectingToday(statementHistory.remainingDue > 0 ? statementHistory.remainingDue : "");
    setPaymentDate(new Date().toISOString().split("T")[0]);
    setPaymentMode("cash");
    setRemarks("");
    setForceShowCollectionForm(true);
    toastr.info("Switched to Collect New Payment mode.");
  };

  const performDelete = async (voucherId) => {
    try {
      setIsSubmitting(true);
      await dispatch(deleteFee(voucherId)).unwrap();
      toastr.success("Receipt voucher deleted successfully");

      if (selectedVoucherId === voucherId) {
        setSelectedVoucherId(null);
        setAmountCollectingToday("");
      }

      dispatch(fetchFees({ page: 1, limit: 200, search: "" }));
    } catch (error) {
      toastr.error(error || "Failed to delete payment voucher");
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestDeleteVoucher = (voucher) => {
    openModal({
      variant: "danger",
      title: "Delete this receipt?",
      message: `This will permanently remove the ₹${parseFloat(voucher.amount || voucher.paid_amount).toFixed(2)} receipt dated ${voucher.payment_date}. The ledger balance for this month will recalculate automatically. This cannot be undone.`,
      confirmLabel: "Delete Receipt",
      cancelLabel: "Keep Receipt",
      onConfirm: () => performDelete(voucher.id),
    });
  };

  // An advance voucher is locked only once the month it targets already has
  // its own recorded activity — meaning that month's balance already relies
  // on this credit. If the target month is still untouched, it's safe to
  // correct or remove the advance from here.
  const isAdvanceLocked = (record) => {
    if (record.payment_type !== "advance") return false;
    const targetM = Number(record.target_month);
    const targetY = Number(record.target_year);
    return fees.some((f) => {
      if (String(f.student_id) !== String(record.student_id)) return false;
      if (String(f.id) === String(record.id)) return false;
      const period = getRecordedPeriod(f);
      return period.month === targetM && period.year === targetY;
    });
  };

  const notifyAdvanceLocked = (action, record) => {
    const targetLabel = `${months.find((m) => Number(m.value) === Number(record.target_month))?.label || ""} ${record.target_year}`;
    openModal({
      variant: "info",
      title: "Advance credit — locked",
      message: `${targetLabel} already has its own payment activity recorded, which relies on this ₹${parseFloat(record.amount || record.paid_amount).toFixed(2)} advance credit. To ${action} it, first review or clear ${targetLabel}'s entries — changing this voucher now would leave that month's balance incorrect.`,
      confirmLabel: "Got it",
      hideCancel: true,
      onConfirm: () => {},
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "month") setMonth(value);
    if (name === "year") setYear(value);
    if (name === "base_fee_amount") {
      setBaseFee(value);
      setIsBaseFeeCustom(true); // Flag custom input
    }
    if (name === "discount_amount") setDiscount(value);
    if (name === "paid_amount") setAmountCollectingToday(value);
    if (name === "advance_extra") setAdvanceExtra(value);
    if (name === "payment_date") setPaymentDate(value);
    if (name === "remarks") setRemarks(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!studentId) {
      toastr.warning("Please select a student profile.");
      return;
    }

    const collectingNow = parseFloat(amountCollectingToday) || 0;
    const extraAdv = parseFloat(advanceExtra) || 0;

    if (collectingNow <= 0 && extraAdv <= 0 && parseFloat(discount) <= 0) {
      toastr.warning("Please enter a valid amount collecting today or advance amount.");
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        student_id: studentId,
        month: parseInt(month, 10),
        year: parseInt(year, 10),
        base_fee_amount: parseFloat(baseFee) || 0,
        discount_amount: parseFloat(discount) || 0,
        advance_adjusted: parseFloat(advanceAdjusted) || 0,
        paid_amount: collectingNow,
        advance_extra: extraAdv,
        payment_date: paymentDate,
        payment_mode: paymentMode,
        remarks: remarks,
      };

      if (selectedVoucherId) {
        await dispatch(updateFee({ id: selectedVoucherId, feeData: payload })).unwrap();
        toastr.success("Payment Voucher Updated Successfully");
      } else {
        await dispatch(collectFee(payload)).unwrap();
        toastr.success("New Payment Voucher Saved Successfully");
      }

      setAmountCollectingToday("");
      setAdvanceExtra("");
      setRemarks("");
      setSelectedVoucherId(null);
      setIsBaseFeeCustom(false);

      dispatch(fetchFees({ page: 1, limit: 200, search: "" }));
    } catch (error) {
      toastr.error(error || "Transaction Processing Failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const periodLabel = `${months.find((m) => Number(m.value) === Number(month))?.label || ""} ${year}`;

  // Three distinct states: a brand new period with no receipts yet, an
  // existing period the admin is adding another voucher to, and a specific
  // voucher actively being corrected.
  const hasExistingPeriodRecords = statementHistory.payments.length > 0;
  const bannerMode = selectedVoucherId ? "update" : hasExistingPeriodRecords ? "existing" : "new";

  // Show the collection form only when there's actually something to do:
  // a brand-new period, a balance still due, an active voucher correction,
  // or the admin explicitly asked to record an extra/advance payment.
  const showCollectionForm =
    !studentId ||
    bannerMode === "new" ||
    selectedVoucherId !== null ||
    statementHistory.remainingDue > 0 ||
    forceShowCollectionForm;

  return (
    <div className="mnc-page-bg py-3 py-md-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8 col-xl-7">

            <div className="mnc-card shadow-sm border">

              {/* MASTER HEADER — unmistakably New vs Update vs Add-to-existing */}
              <div className={`mnc-mode-banner ${bannerMode === "new" ? "is-new" : "is-update"}`}>
                <div className="mnc-mode-banner-main">
                  <span className="mnc-mode-eyebrow">
                    {bannerMode === "update"
                      ? "Editing an existing receipt"
                      : bannerMode === "existing"
                      ? "This period already has receipts logged"
                      : "Recording a new receipt"}
                  </span>
                  <h4 className="mnc-mode-title">
                    {bannerMode === "new" ? "New Collection" : "Update Collection"}
                    {studentId && <span className="mnc-mode-period"> · {periodLabel}</span>}
                  </h4>
                  <p className="mnc-mode-sub">
                    {selectedStudent
                      ? bannerMode === "update"
                        ? `Changes will overwrite receipt #${selectedVoucherId} for ${selectedStudent.name}.`
                        : bannerMode === "existing"
                        ? `${selectedStudent.name} already has ${statementHistory.payments.length} receipt${statementHistory.payments.length > 1 ? "s" : ""} for ${periodLabel}. Saving now adds another voucher to this ledger.`
                        : `A new payment voucher will be logged for ${selectedStudent.name}.`
                      : "Select a student below to begin."}
                  </p>
                </div>

                {selectedVoucherId && (
                  <button
                    type="button"
                    className="btn-mnc-ghost flex-shrink-0"
                    onClick={handleSwitchToNewPayment}
                  >
                    + New Voucher Instead
                  </button>
                )}
              </div>

              {/* ACTION TRANSACTION FORM */}
              <form onSubmit={handleSubmit} className="p-3 p-md-4 bg-white">
                <div className="row g-3 g-md-4">

                  {/* STUDENT COMBOBOX */}
                  <div className="col-12">
                    <label className="mnc-label">1. Student Target Profile *</label>
                    <StudentCombobox
                      students={students}
                      value={studentId}
                      onSelect={(id) => setStudentId(id)}
                      disabled={isSubmitting || selectedVoucherId !== null}
                    />
                    {selectedStudent && (
                      <div className="mnc-selected-chip mt-2">
                        <span className="mnc-selected-chip-dot" />
                        {selectedStudent.name} · {selectedStudent.admission_number}
                      </div>
                    )}
                  </div>

                  {/* STATEMENT MONTH & YEAR */}
                  <div className="col-6 col-md-6">
                    <label className="mnc-label">2. Statement Month *</label>
                    <select
                      name="month"
                      className="mnc-field fw-600"
                      value={month}
                      disabled={selectedVoucherId !== null}
                      onChange={handleChange}
                    >
                      {months.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-6 col-md-6">
                    <label className="mnc-label">Statement Year *</label>
                    <input
                      type="number"
                      className="mnc-field fw-600"
                      value={year}
                      disabled={selectedVoucherId !== null}
                      onChange={handleChange}
                      placeholder="e.g. 2026"
                      inputMode="numeric"
                    />
                  </div>

                  {/* UNIFIED LEDGER SUMMARY CARD */}
                  {studentId && (
                    <div className="col-12">
                      <div className="mnc-ledger-card">
                        <div className="mnc-ledger-head">
                          <span className="mnc-ledger-head-label">Ledger Status · {periodLabel}</span>
                          {statementHistory.remainingDue > 0 ? (
                            <span className="mnc-status-pill mnc-status-danger">Partially Paid</span>
                          ) : statementHistory.baseFee > 0 ? (
                            <span className="mnc-status-pill mnc-status-success">Full Month Cleared</span>
                          ) : (
                            <span className="mnc-status-pill mnc-status-muted">No Fee Set</span>
                          )}
                        </div>

                        <div className="mnc-ledger-rows">
                          <div className="mnc-ledger-row">
                            <span>Base fee</span>
                            <strong>₹{statementHistory.baseFee.toFixed(2)}</strong>
                          </div>
                          {statementHistory.discount > 0 && (
                            <div className="mnc-ledger-row text-danger">
                              <span>Discount / scholarship</span>
                              <strong>−₹{statementHistory.discount.toFixed(2)}</strong>
                            </div>
                          )}
                          {statementHistory.advanceCredit > 0 && (
                            <div className="mnc-ledger-row mnc-ledger-row-info">
                              <span>Advance credit applied</span>
                              <strong>−₹{statementHistory.advanceCredit.toFixed(2)}</strong>
                            </div>
                          )}
                          {statementHistory.directPaid > 0 && (
                            <div className="mnc-ledger-row mnc-ledger-row-success">
                              <span>Direct tuition paid</span>
                              <strong>−₹{statementHistory.directPaid.toFixed(2)}</strong>
                            </div>
                          )}
                        </div>

                        <div className="mnc-ledger-balance">
                          <span>Balance due</span>
                          <span className={statementHistory.remainingDue > 0 ? "text-danger" : "text-success"}>
                            ₹{statementHistory.remainingDue.toFixed(2)}
                          </span>
                        </div>

                        {/* This-transaction strip — only shown when there's actually something being collected */}
                        {financialSummary.totalReceiptPayable > 0 && (
                          <div className="mnc-transaction-strip">
                            <span>Collecting in this voucher</span>
                            <strong>₹{financialSummary.totalReceiptPayable.toFixed(2)}</strong>
                          </div>
                        )}

                        {/* LOGGED VOUCHERS */}
                        {statementHistory.payments.length > 0 && (
                          <div className="mnc-voucher-list-wrap">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="mnc-ledger-head-label">Logged Receipts for Period</span>
                              {selectedVoucherId && (
                                <button
                                  type="button"
                                  className="btn-mnc-link"
                                  onClick={handleSwitchToNewPayment}
                                >
                                  + Add New Voucher
                                </button>
                              )}
                            </div>

                            <div className="d-flex flex-column gap-2">
                              {statementHistory.payments.map((p) => {
                                const isEditing = String(p.id) === String(selectedVoucherId);
                                const isAdv = p.payment_type === "advance";
                                const locked = isAdv && isAdvanceLocked(p);

                                return (
                                  <div
                                    key={p.id}
                                    className={`mnc-voucher-item d-flex align-items-center justify-content-between ${isEditing ? "is-editing" : ""} ${locked ? "is-locked" : ""}`}
                                  >
                                    <div className="d-flex align-items-center gap-3">
                                      <div className="mnc-voucher-icon">
                                        {isAdv ? (
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="11" width="18" height="10" rx="2" />
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                          </svg>
                                        ) : (
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="2" y="5" width="20" height="14" rx="2" />
                                            <line x1="2" y1="10" x2="22" y2="10" />
                                          </svg>
                                        )}
                                      </div>
                                      <div>
                                        <div className="d-flex align-items-center gap-2">
                                          <span className="fw-800 text-dark fs-6">₹{parseFloat(p.amount || p.paid_amount).toFixed(2)}</span>
                                          <span className={`mnc-badge-pill ${isAdv ? "bg-info-light text-info" : "bg-slate-100 text-slate-700"}`}>
                                            {isAdv ? `Advance → ${months.find((m) => Number(m.value) === Number(p.target_month))?.label || ""} ${p.target_year}` : (p.payment_mode || "cash").toUpperCase()}
                                          </span>
                                          {locked && (
                                            <span className="mnc-lock-tag" title="Locked — target month already has activity">
                                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <rect x="4" y="11" width="16" height="9" rx="1.5" />
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                              </svg>
                                              Locked
                                            </span>
                                          )}
                                        </div>
                                        <span className="text-muted fs-7 d-block">{p.payment_date}</span>
                                      </div>
                                    </div>

                                    <div className="d-flex align-items-center gap-1">
                                      <button
                                        type="button"
                                        className={`btn-mnc-action btn-mnc-edit ${isEditing ? "is-active" : ""}`}
                                        title={locked ? "Locked — target month already has activity" : "Edit Voucher"}
                                        onClick={() => {
                                          if (locked) {
                                            notifyAdvanceLocked("edit", p);
                                            return;
                                          }
                                          setSelectedVoucherId(p.id);
                                          setAmountCollectingToday(p.amount || p.paid_amount);
                                          setPaymentDate(p.payment_date || new Date().toISOString().split("T")[0]);
                                          setPaymentMode(p.payment_mode || "cash");
                                          setRemarks(p.remarks || "");
                                        }}
                                      >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                        </svg>
                                        <span>{isEditing ? "Active" : "Edit"}</span>
                                      </button>

                                      <button
                                        type="button"
                                        className="btn-mnc-action btn-mnc-delete"
                                        title={locked ? "Locked — target month already has activity" : "Delete Voucher"}
                                        onClick={() => (locked ? notifyAdvanceLocked("delete", p) : requestDeleteVoucher(p))}
                                        disabled={isSubmitting}
                                      >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <polyline points="3 6 5 6 21 6" />
                                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                        <span>Delete</span>
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {studentId && !showCollectionForm && (
                    <div className="col-12">
                      <div className="mnc-settled-panel">
                        <div className="mnc-settled-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        </div>
                        <div className="mnc-settled-text">
                          <strong>This month is fully settled</strong>
                          <p>No balance is due for {periodLabel} — there's nothing to collect right now.</p>
                        </div>
                        <button
                          type="button"
                          className="btn-mnc-ghost flex-shrink-0"
                          onClick={() => setForceShowCollectionForm(true)}
                        >
                          + Record Extra / Advance Payment
                        </button>
                      </div>
                    </div>
                  )}

                  {showCollectionForm && (
                    <>
                  <div className="col-12">
                    <div className="mnc-section-divider">
                      <span>Fee Breakdown &amp; Ledger Adjustments</span>
                    </div>
                  </div>

                  {/* BASE FEE AMOUNT */}
                  <div className="col-12 col-md-6">
                    <label className="mnc-label">Base Fee Amount (₹) *</label>
                    <div className="input-group-mnc">
                      <span className="input-icon-prefix">₹</span>
                      <input
                        type="number"
                        name="base_fee_amount"
                        className="mnc-field ps-5 fw-700 text-dark"
                        value={baseFee}
                        onChange={handleChange}
                        placeholder="e.g. 4800.00"
                        min="0"
                        inputMode="decimal"
                      />
                    </div>
                    <span className="text-muted fs-7 mt-1 d-block">Monthly coaching fee for this period.</span>
                  </div>

                  {/* DISCOUNT AMOUNT */}
                  <div className="col-12 col-md-6">
                    <label className="mnc-label">Discount / Scholarship (₹)</label>
                    <div className="input-group-mnc">
                      <span className="input-icon-prefix">₹</span>
                      <input
                        type="number"
                        name="discount_amount"
                        className="mnc-field ps-5 fw-700 text-danger"
                        value={discount}
                        onChange={handleChange}
                        placeholder="0.00"
                        min="0"
                        inputMode="decimal"
                      />
                    </div>
                    <span className="text-muted fs-7 mt-1 d-block">Waiver or fee deduction.</span>
                  </div>

                  {/* ADVANCE ADJUSTED */}
                  <div className="col-12 col-md-6">
                    <label className="mnc-label">Advance Adjusted (₹)</label>
                    <div className="input-group-mnc">
                      <span className="input-icon-prefix">₹</span>
                      <input
                        type="number"
                        name="advance_adjusted"
                        className="mnc-field ps-5 fw-700 text-primary bg-light"
                        value={advanceAdjusted}
                        readOnly
                        placeholder="0.00"
                      />
                    </div>
                    <span className="text-muted fs-7 mt-1 d-block">Advance credit applied from prior receipt.</span>
                  </div>

                  {/* AMOUNT PAID TODAY */}
                  <div className="col-12 col-md-6">
                    <label className="mnc-label text-success">
                      {selectedVoucherId ? "Voucher Corrected Amount (₹) *" : "Amount Paid Today (₹) *"}
                    </label>
                    <div className="input-group-mnc">
                      <span className="input-icon-prefix">₹</span>
                      <input
                        type="number"
                        name="paid_amount"
                        className="mnc-field ps-5 fw-700 text-success border-success"
                        value={amountCollectingToday}
                        onChange={handleChange}
                        placeholder="e.g. 4800.00"
                        min="0"
                        inputMode="decimal"
                      />
                    </div>
                    <span className="text-muted fs-7 mt-1 d-block">Cash/UPI collected toward this month.</span>
                  </div>

                  {/* ADVANCE EXTRA FOR NEXT MONTH */}
                  <div className="col-12 col-md-6">
                    <label className="mnc-label text-info">Advance Received for Future (₹)</label>
                    <div className="input-group-mnc">
                      <span className="input-icon-prefix">₹</span>
                      <input
                        type="number"
                        name="advance_extra"
                        className="mnc-field ps-5 fw-700 text-info border-info"
                        value={advanceExtra}
                        onChange={handleChange}
                        placeholder="0.00"
                        min="0"
                        disabled={selectedVoucherId !== null}
                        inputMode="decimal"
                      />
                    </div>
                    <span className="text-muted fs-7 mt-1 d-block">Extra payment assigned to future month.</span>
                  </div>

                  {/* LIVE TRANSACTION PREVIEW — plain-language confirmation before saving */}
                  <div className="col-12">
                    <div className={`mnc-preview mnc-preview-${transactionPreview.tone}`}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mnc-preview-icon">
                        <circle cx="12" cy="12" r="9" />
                        <line x1="12" y1="8" x2="12" y2="12.5" />
                        <circle cx="12" cy="16" r="0.6" fill="currentColor" stroke="none" />
                      </svg>
                      <span>{transactionPreview.text}</span>
                    </div>
                  </div>

                  {/* PAYMENT DATE */}
                  <div className="col-6 col-md-6">
                    <label className="mnc-label">Transaction Settlement Date</label>
                    <input
                      type="date"
                      name="payment_date"
                      className="mnc-field fw-600"
                      value={paymentDate}
                      onChange={handleChange}
                    />
                  </div>

                  {/* PAYMENT CHANNEL MODE */}
                  <div className="col-6 col-md-6">
                    <label className="mnc-label">Payment Mode *</label>
                    <select
                      className="mnc-field fw-600"
                      value={paymentMode}
                      onChange={(e) => setPaymentMode(e.target.value)}
                    >
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="bank">Bank Transfer</option>
                      <option value="card">Card / POS</option>
                    </select>
                  </div>

                  {/* REMARKS */}
                  <div className="col-12">
                    <label className="mnc-label">Administrative Notes &amp; Audit Remarks</label>
                    <textarea
                      rows="2"
                      name="remarks"
                      className="mnc-field"
                      value={remarks}
                      onChange={handleChange}
                      placeholder="Input any additional voucher tracking notes or bank reference numbers here..."
                    />
                  </div>
                    </>
                  )}

                </div>

                {/* FOOTER ACTIONS */}
                {showCollectionForm && (
                <div className="mnc-footer d-flex flex-wrap justify-content-end gap-2 gap-md-3 mt-4 pt-3 border-top bg-white">
                  <button
                    type="button"
                    className="btn-mnc-discard"
                    onClick={handleReset}
                    disabled={isSubmitting}
                  >
                    Clear
                  </button>
                  <button
                    type="submit"
                    className={`btn-mnc-sync ${selectedVoucherId ? "btn-mnc-update" : ""}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>Syncing Ledger...</>
                    ) : selectedVoucherId ? (
                      "Update Ledger Record"
                    ) : (
                      "Save Ledger Record"
                    )}
                  </button>
                </div>
                )}
              </form>

            </div>

          </div>
        </div>
      </div>

      <ConfirmModal open={modalOpen} config={modalConfig} onClose={closeModal} />

      <style>{`
        :root {
          --mnc-ink: #1d1d1f;
          --mnc-ink-soft: #6e6e73;
          --mnc-border: #d9dce1;
          --mnc-surface-muted: #f5f5f7;
          --mnc-accent: #0071e3;
          --mnc-accent-dark: #0058b0;
          --mnc-success: #0f7a41;
          --mnc-success-bg: #e6f6ec;
          --mnc-danger: #d92d20;
          --mnc-danger-bg: #fdeeed;
          --mnc-warning: #b45309;
          --mnc-warning-bg: #fff6e6;
          --mnc-info: #0369a1;
          --mnc-info-bg: #eaf6fd;
        }

        .mnc-page-bg { background: #f4f5f7; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif; color: var(--mnc-ink); }
        .fw-800 { font-weight: 800; }
        .fw-700 { font-weight: 700; }
        .fw-600 { font-weight: 600; }
        .fs-7 { font-size: 0.75rem; }
        .uppercase { text-transform: uppercase; }
        .bg-info-light { background: var(--mnc-info-bg); color: var(--mnc-info); }

        .mnc-card { background: #fff; border-radius: 18px; overflow: hidden; box-shadow: 0 20px 40px -20px rgba(15, 23, 42, 0.12); border: 1px solid #ebebee !important; }

        /* MODE BANNER */
        .mnc-mode-banner { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 14px; padding: 24px 28px; border-bottom: 1px solid #ebebee; }
        .mnc-mode-banner.is-new { background: linear-gradient(135deg, #f2f8ff 0%, #ffffff 65%); }
        .mnc-mode-banner.is-update { background: linear-gradient(135deg, #fff8ec 0%, #ffffff 65%); }
        .mnc-mode-eyebrow { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
        .is-new .mnc-mode-eyebrow { color: var(--mnc-accent); }
        .is-update .mnc-mode-eyebrow { color: var(--mnc-warning); }
        .mnc-mode-title { font-size: 1.55rem; font-weight: 700; letter-spacing: -0.02em; margin: 2px 0 4px; color: var(--mnc-ink); }
        .mnc-mode-period { font-weight: 500; color: var(--mnc-ink-soft); }
        .mnc-mode-sub { font-size: 0.86rem; color: var(--mnc-ink-soft); margin: 0; max-width: 46ch; }

        .btn-mnc-ghost { background: #fff; border: 1px solid var(--mnc-border); color: var(--mnc-ink); border-radius: 980px; padding: 10px 18px; font-weight: 600; font-size: 0.82rem; }
        .btn-mnc-ghost:hover { background: var(--mnc-surface-muted); }

        .btn-mnc-sync { background: var(--mnc-accent); color: #fff; border: none; border-radius: 980px; padding: 12px 28px; font-weight: 600; font-size: 0.85rem; transition: background-color 0.2s ease; min-height: 44px; }
        .btn-mnc-sync:hover:not(:disabled) { background: var(--mnc-accent-dark); }
        .btn-mnc-sync.btn-mnc-update { background: var(--mnc-warning); }
        .btn-mnc-sync.btn-mnc-update:hover:not(:disabled) { background: #92400a; }

        .btn-mnc-danger { background: var(--mnc-danger); color: #fff; border: none; border-radius: 980px; padding: 12px 24px; font-weight: 600; font-size: 0.85rem; min-height: 44px; }
        .btn-mnc-danger:hover { background: #b8251a; }

        .btn-mnc-discard { background: white; border: 1px solid var(--mnc-border); color: var(--mnc-ink-soft); border-radius: 980px; padding: 12px 24px; font-weight: 600; font-size: 0.85rem; min-height: 44px; }
        .btn-mnc-discard:hover:not(:disabled) { background: var(--mnc-surface-muted); }

        .btn-mnc-link { background: transparent; border: none; color: var(--mnc-accent); font-weight: 700; font-size: 0.75rem; padding: 0; }

        .mnc-label { display: block; margin-bottom: 6px; font-size: .68rem; font-weight: 700; color: var(--mnc-ink-soft); text-transform: uppercase; letter-spacing: 0.05em; }

        .mnc-field { width: 100%; border: 1px solid var(--mnc-border); border-radius: 10px; padding: 11px 14px; font-size: 0.88rem; color: #334155; background-color: #fff; transition: border-color 0.2s ease, box-shadow 0.2s ease; min-height: 44px; }
        .mnc-field:focus { outline: none; border-color: var(--mnc-accent); box-shadow: 0 0 0 3px rgba(0, 113, 227, 0.14); }

        .mnc-section-divider { display: flex; align-items: center; gap: 10px; font-weight: 700; color: var(--mnc-ink); font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.04em; padding-bottom: 10px; border-bottom: 1px solid #ececef; }

        .input-group-mnc { position: relative; width: 100%; }
        .input-icon-prefix { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); font-weight: 700; color: #94a3b8; pointer-events: none; }

        .mnc-combobox { position: relative; width: 100%; }
        .mnc-combobox-field { position: relative; display: flex; align-items: center; }
        .mnc-combobox-icon { position: absolute; left: 14px; color: #94a3b8; pointer-events: none; }
        .mnc-combobox-input { padding-left: 38px; padding-right: 34px; }
        .mnc-combobox-clear { position: absolute; right: 8px; width: 26px; height: 26px; border: none; background: #f1f5f9; color: #64748b; border-radius: 50%; font-size: 1rem; cursor: pointer; }

        .mnc-combobox-list { position: absolute; z-index: 40; top: calc(100% + 6px); left: 0; right: 0; max-height: 260px; overflow-y: auto; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 12px 28px -8px rgba(15, 23, 42, 0.18); margin: 0; padding: 6px; list-style: none; }
        .mnc-combobox-option { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 8px; cursor: pointer; font-size: 0.86rem; color: #334155; }
        .mnc-combobox-option.is-active { background: #eef4ff; }

        .mnc-selected-chip { display: inline-flex; align-items: center; gap: 8px; font-size: 0.78rem; font-weight: 700; color: var(--mnc-accent-dark); background: #eaf3ff; padding: 6px 12px; border-radius: 999px; }
        .mnc-selected-chip-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--mnc-accent); }

        /* LEDGER CARD */
        .mnc-ledger-card { border: 1px solid #ececef; border-radius: 14px; padding: 18px 20px; background: var(--mnc-surface-muted); }
        .mnc-ledger-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
        .mnc-ledger-head-label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--mnc-ink-soft); }

        .mnc-status-pill { font-size: 0.7rem; font-weight: 700; padding: 5px 11px; border-radius: 999px; }
        .mnc-status-danger { background: var(--mnc-danger-bg); color: var(--mnc-danger); }
        .mnc-status-success { background: var(--mnc-success-bg); color: var(--mnc-success); }
        .mnc-status-muted { background: #ececef; color: var(--mnc-ink-soft); }

        .mnc-ledger-rows { display: flex; flex-direction: column; gap: 4px; padding-bottom: 8px; }
        .mnc-ledger-row { display: flex; justify-content: space-between; font-size: 0.83rem; color: #475569; padding: 3px 0; }
        .mnc-ledger-row-info { color: var(--mnc-info); }
        .mnc-ledger-row-success { color: var(--mnc-success); }

        .mnc-ledger-balance { display: flex; justify-content: space-between; align-items: baseline; padding-top: 10px; border-top: 1px solid #e2e4e9; font-weight: 700; font-size: 1.15rem; }
        .mnc-ledger-balance span:first-child { font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--mnc-ink-soft); font-weight: 700; }

        .mnc-transaction-strip { margin-top: 12px; display: flex; justify-content: space-between; align-items: center; background: #eaf3ff; border: 1px solid #d3e6ff; border-radius: 10px; padding: 10px 14px; font-size: 0.85rem; color: var(--mnc-accent-dark); font-weight: 700; }

        .mnc-settled-panel { display: flex; align-items: center; gap: 14px; border: 1px solid #d3ecdd; background: var(--mnc-success-bg); border-radius: 14px; padding: 16px 18px; flex-wrap: wrap; }
        .mnc-settled-icon { width: 38px; height: 38px; border-radius: 50%; background: #fff; color: var(--mnc-success); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .mnc-settled-text { flex: 1; min-width: 200px; }
        .mnc-settled-text strong { display: block; color: var(--mnc-ink); font-size: 0.92rem; margin-bottom: 2px; }
        .mnc-settled-text p { margin: 0; font-size: 0.8rem; color: var(--mnc-ink-soft); }

        .mnc-voucher-list-wrap { margin-top: 16px; padding-top: 14px; border-top: 1px solid #e2e4e9; }

        .mnc-voucher-item { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 10px 12px; transition: all 0.15s ease; }
        .mnc-voucher-item:hover { border-color: #cbd5e1; box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04); }
        .mnc-voucher-item.is-editing { background: #fffbeb; border-color: #f0b429; }
        .mnc-voucher-item.is-locked { background: #fafbfd; }

        .mnc-voucher-icon { width: 34px; height: 34px; border-radius: 9px; background: #f1f5f9; color: #64748b; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .mnc-badge-pill { font-size: 0.65rem; font-weight: 800; padding: 2px 8px; border-radius: 5px; letter-spacing: 0.03em; }
        .mnc-lock-tag { display: inline-flex; align-items: center; gap: 3px; font-size: 0.62rem; font-weight: 800; color: var(--mnc-ink-soft); background: #ececef; padding: 2px 7px; border-radius: 5px; text-transform: uppercase; letter-spacing: 0.03em; }

        .btn-mnc-action { display: inline-flex; align-items: center; gap: 4px; font-size: 0.75rem; font-weight: 700; padding: 5px 10px; border-radius: 7px; border: 1px solid transparent; transition: all 0.15s ease; cursor: pointer; background: transparent; }
        .btn-mnc-action:disabled { opacity: 0.45; cursor: not-allowed; }

        .btn-mnc-edit { color: var(--mnc-accent-dark); border-color: #cfe2ff; background: #eaf3ff; }
        .btn-mnc-edit:hover { background: #dcebff; }
        .btn-mnc-edit.is-active { background: var(--mnc-warning); border-color: var(--mnc-warning); color: #ffffff; }

        .btn-mnc-delete { color: var(--mnc-danger); border-color: #f7cdc8; background: var(--mnc-danger-bg); }
        .btn-mnc-delete:hover { background: #fbdcd8; }

        /* LIVE PREVIEW */
        .mnc-preview { display: flex; align-items: flex-start; gap: 10px; border-radius: 10px; padding: 12px 14px; font-size: 0.83rem; font-weight: 500; line-height: 1.45; }
        .mnc-preview-icon { flex-shrink: 0; margin-top: 1px; }
        .mnc-preview-muted { background: var(--mnc-surface-muted); color: var(--mnc-ink-soft); }
        .mnc-preview-success { background: var(--mnc-success-bg); color: var(--mnc-success); }
        .mnc-preview-warning { background: var(--mnc-warning-bg); color: var(--mnc-warning); }
        .mnc-preview-info { background: var(--mnc-info-bg); color: var(--mnc-info); }

        /* MODAL */
        .mnc-modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); backdrop-filter: blur(2px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; }
        .mnc-modal { background: #fff; border-radius: 18px; padding: 28px 26px 22px; max-width: 400px; width: 100%; box-shadow: 0 30px 60px -15px rgba(15, 23, 42, 0.35); outline: none; }
        .mnc-modal-icon { width: 46px; height: 46px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; }
        .mnc-modal-icon-danger { background: var(--mnc-danger-bg); color: var(--mnc-danger); }
        .mnc-modal-icon-info { background: var(--mnc-info-bg); color: var(--mnc-info); }
        .mnc-modal-title { font-size: 1.1rem; font-weight: 700; color: var(--mnc-ink); margin-bottom: 6px; }
        .mnc-modal-message { font-size: 0.87rem; color: var(--mnc-ink-soft); line-height: 1.55; margin-bottom: 20px; }
        .mnc-modal-actions { display: flex; justify-content: flex-end; gap: 10px; }

        @media (max-width: 576px) {
          .mnc-card { border-radius: 14px; }
          .mnc-mode-banner { padding: 18px 18px; }
          .mnc-mode-title { font-size: 1.28rem; }
          .mnc-footer { position: sticky; bottom: 0; background: #fff; z-index: 10; padding-bottom: 12px; }
          .btn-mnc-discard, .btn-mnc-sync { flex: 1; text-align: center; justify-content: center; }
          .mnc-modal { padding: 22px 18px 18px; }
          .mnc-modal-actions { flex-direction: column-reverse; }
          .mnc-modal-actions button { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default Fees;
